import { collection } from '@/infra/collection'
import { UserData, type UserDataProps } from '@/features/iam/users/UserData'
import { Id } from '@/infra/id'
import { type IamApi } from '@/features/iam/api/iam-api'
import { type UserDataResource } from '@/features/iam/api/resources/users'
import { type Observable, switchMap } from 'rxjs'
import { type WorkspaceMember } from '@/features/iam/members/WorkspaceMember'
import { shareLatest, withAbortSignal } from '@/utils/rx-util'
import { memoize } from '@/utils/memoize-util'

/**
 * Manages the user data (separate from the actual user).
 */
export class UserDataStore {
  readonly userData = collection({
    getDataId: (d) => d.id,
    getModelId: (m) => m.id,
    create: () => new UserData(),
  })

  /**
   * Emits the current user data when we've logged in and chosen a member.
   */
  readonly currentUserData$

  constructor(
    currentMember$: Observable<WorkspaceMember>,
    private readonly _api: IamApi,
  ) {
    this.currentUserData$ = currentMember$.pipe(
      switchMap((member) =>
        withAbortSignal((signal) => this.fetchUserData(member.userId, signal)),
      ),
      shareLatest(),
    )
    this.fetchUserData = memoize(this.fetchUserData.bind(this), {
      promise: true,
    })
  }

  async fetchUserData(id: Id, signal?: AbortSignal) {
    const resource = await this._api.userData.get(Id.assert(id), signal)
    signal?.throwIfAborted()
    const props = mapResourceToProps(resource)
    return this.userData.set(props)
  }
}

function mapResourceToProps(resource: UserDataResource): UserDataProps {
  return {
    id: Id.assert(resource.user_public_id),
    workspaceSlugs: resource.workspace_slugs ?? [],
    type: resource.type,
  }
}
